/**
 * 承認案件一覧の本体 (フィルタバー + テーブル + 承認/差戻しダイアログ)。
 *
 * `/admin/approval-tasks` ページ本体と、 spot 入札画面の確認ステップ下部の
 * 両方から再利用する共有コンポーネント。 props で 業務フィルタの有無 /
 * 表示する承認状態の絞り込みを制御する。
 */
import { type ReactNode, useMemo, useState } from 'react'

import { timestampDate } from '@bufbuild/protobuf/wkt'
import { BusinessProcess } from '@ebalance/proto/bfftypes/v1/approval_flow_type_pb'
import type { ApprovalTask } from '@ebalance/proto/bffuser/v1/approval_task_model_pb'
import {
  BiddingApprovalState,
  BiddingLifecycleAction,
} from '@ebalance/proto/bffuser/v1/bidding_common_model_pb'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import {
  Box,
  PrimaryButton,
  SecondaryButton,
  Select,
} from '@enechain/ecloud-designsystem'
import type { SelectOption } from '@enechain/ecloud-designsystem'

import { RejectDialog } from '~/components/bidding/common/RejectDialog'
import type { ViolationSection } from '~/components/bidding/common/SubmitConfirmModal'
import { CompactAlert } from '~/components/common/CompactAlert'
import {
  MasterDataTable,
  type MasterDataTableColumn,
} from '~/components/common/MasterDataTable'
import { RegionLoading } from '~/components/common/RegionLoading'
import { FilterSection, FilterToolbar } from '~/components/common/filters'
import { selectFontFix } from '~/components/common/filters/selectFontFix'
import { useTransitionBiddingBlockApprovalState } from '~/hooks/bidding/useTransitionBiddingBlockApprovalState'
import { useTransitionBiddingIntradayOrderState } from '~/hooks/bidding/useTransitionBiddingIntradayOrderState'
import { useTransitionBiddingSpotApprovalState } from '~/hooks/bidding/useTransitionBiddingSpotApprovalState'
import { useMemberDisplayName } from '~/hooks/member/useMemberDisplayName'
import { useErrorToast } from '~/hooks/useErrorToast'
import { useSuccessToast } from '~/hooks/useSuccessToast'
import { now as serverNow } from '~/models/clock'
import { formatMonthDayTime } from '~/models/dateTime'
import { ApproveDialog } from '~/pages/AdminApprovalTasks/components/ApproveDialog'
import { ApproverGuardModal } from '~/pages/AdminApprovalTasks/components/ApproverGuardModal'
import { ApproverRowNotice } from '~/pages/AdminApprovalTasks/components/ApproverRowNotice'
import { approvalTasksQueryKeys } from '~/pages/AdminApprovalTasks/hooks/queryKeys'
import { useIntradayViolationSection } from '~/pages/AdminApprovalTasks/hooks/useIntradayViolationSection'
import { useListApprovalTasks } from '~/pages/AdminApprovalTasks/hooks/useListApprovalTasks'
import { useSpotBlockViolationSections } from '~/pages/AdminApprovalTasks/hooks/useSpotBlockViolationSections'
import type { DecisionAction } from '~/pages/AdminApprovalTasks/models/approvalDecisionFailure'
import {
  classifyDecisionFailure,
  closedDecisionMessage,
  isPastSpotDeadline,
} from '~/pages/AdminApprovalTasks/models/approvalDecisionFailure'
import { buildApproverGuardMessages } from '~/pages/AdminApprovalTasks/models/approverGuardMessages'
import {
  DEFAULT_FILTER,
  filterApprovalTasks,
} from '~/pages/AdminApprovalTasks/models/filter'
import type { ApprovalTaskFilter } from '~/pages/AdminApprovalTasks/models/filter'
import { useApproverIdentity } from '~/pages/SpotBidding/hooks/useApproverIdentity'
import { buildApproverStandingNotices } from '~/pages/SpotBidding/models/approverIdentity'

/** 注意 (ApproverRowNotice の最大幅 320px) が折り返さずに入る操作列の幅。 */
const ROW_NOTICE_ACTION_COL_WIDTH = '344px'

type Props = {
  /** 業務 (BusinessProcess) フィルタを非表示にする (= spot 埋め込み時)。 */
  hideBusinessFilter?: boolean
  /** 状態 (承認状態) フィルタを非表示にする (= spot 埋め込み時)。 */
  hideStateFilter?: boolean
  /**
   * 表示を許可する承認状態。 指定すると状態フィルタの選択肢もこの集合に絞り、
   * 「すべて」選択時もこの集合のみ表示する (例: spot 埋め込みは [PENDING, REJECTED])。
   * 未指定なら全状態。
   */
  allowedStates?: BiddingApprovalState[]
  /**
   * 表示を許可する業務 (BusinessProcess)。 指定するとこの集合のみ表示する
   * (例: spot 埋め込みは [SPOT_BID] で intraday 案件の混入を防ぐ)。 未指定なら全業務。
   */
  allowedBusinessProcesses?: BusinessProcess[]
  /**
   * read-only 表示 (= 承認/差戻しの行アクションを出さない)。 spot 埋め込みは申請後の
   * 状況可視化のみが目的で、 承認操作は `/admin/approval-tasks` に集約するため true。
   */
  readOnly?: boolean
}

function formatRequestedAt(task: ApprovalTask): string {
  if (task.requestedAt === undefined) return '—'
  return formatMonthDayTime(timestampDate(task.requestedAt))
}

type DialogState =
  | { kind: 'none' }
  | { kind: 'approve'; task: ApprovalTask }
  | { kind: 'reject'; task: ApprovalTask }

/** 承認者でない人・申請者本人への確認モーダル (AC-APT-019a) の対象。 確定すると同じ操作のダイアログへ進む。 */
type GuardState = { action: DecisionAction; task: ApprovalTask } | undefined

function getApprovalStateLabel(
  state: BiddingApprovalState,
  t: (key: string) => string,
): string {
  switch (state) {
    case BiddingApprovalState.PENDING:
      return t('page:adminApprovalTasks.approvalState.pending')
    case BiddingApprovalState.APPROVED:
      return t('page:adminApprovalTasks.approvalState.approved')
    case BiddingApprovalState.REJECTED:
      return t('page:adminApprovalTasks.approvalState.rejected')
    default:
      return '—'
  }
}

export const ApprovalTaskList = ({
  hideBusinessFilter = false,
  hideStateFilter = false,
  allowedStates,
  allowedBusinessProcesses,
  readOnly = false,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const showSuccessToast = useSuccessToast()
  const showErrorToast = useErrorToast()
  // requested_by / approved_by は eauth member ID。表示名に解決し、未解決時は
  // 生 ID を出さず '—' (useMemberDisplayName の露出防止規約)
  const resolveDisplayName = useMemberDisplayName()

  const [filter, setFilter] = useState<ApprovalTaskFilter>(
    // allowedStates 指定時は「すべて (= 許可集合すべて)」を初期表示にする。
    allowedStates !== undefined
      ? { approvalState: undefined, businessProcess: undefined }
      : DEFAULT_FILTER,
  )
  const [dialogState, setDialogState] = useState<DialogState>({ kind: 'none' })
  const [guardState, setGuardState] = useState<GuardState>(undefined)
  // (a)(b) 承認・差戻しが締切 / GC の後で実行されなかった時の、 一覧の画面のエラー (AC-APT-022)。
  const [listError, setListError] = useState<string | undefined>(undefined)
  // (c)(d) 承認が実行されなかった理由。 承認確認ダイアログを開いたまま出す (AC-APT-022)。
  const [approveBlockedSections, setApproveBlockedSections] = useState<
    ViolationSection[]
  >([])
  const approverIdentity = useApproverIdentity()
  const buildIntradayViolationSection = useIntradayViolationSection()
  const buildSpotBlockViolationSections = useSpotBlockViolationSections()

  const { data, isLoading } = useListApprovalTasks()

  const filteredTasks = useMemo(() => {
    if (data === undefined) return []
    let base = data.approvalTasks
    if (allowedStates !== undefined) {
      base = base.filter((task) => allowedStates.includes(task.approvalState))
    }
    if (allowedBusinessProcesses !== undefined) {
      base = base.filter((task) =>
        allowedBusinessProcesses.includes(task.businessProcess),
      )
    }
    return filterApprovalTasks(base, filter)
  }, [data, filter, allowedStates, allowedBusinessProcesses])

  const { mutateAsync: transitionSpot, isPending: isSpotPending } =
    useTransitionBiddingSpotApprovalState()
  const { mutateAsync: transitionIntraday, isPending: isIntradayPending } =
    useTransitionBiddingIntradayOrderState()
  const { mutateAsync: transitionBlock, isPending: isBlockPending } =
    useTransitionBiddingBlockApprovalState()

  const isActionPending = isSpotPending || isIntradayPending || isBlockPending

  const invalidateApprovalTasks = async (): Promise<void> => {
    await queryClient.invalidateQueries({
      queryKey: approvalTasksQueryKeys.list,
    })
  }

  /** 承認 / 差戻しの RPC を、 案件の業務に応じた service へ送る。 */
  const transitionTask = async (
    task: ApprovalTask,
    action: DecisionAction,
    reason: string | undefined,
  ): Promise<void> => {
    const request = {
      id: task.id,
      action:
        action === 'approve'
          ? BiddingLifecycleAction.APPROVE
          : BiddingLifecycleAction.REJECT,
      reason,
      expectedUpdatedAt: task.updatedAt,
    }
    if (task.businessProcess === BusinessProcess.SPOT_BID) {
      await transitionSpot(request)
    } else if (task.businessProcess === BusinessProcess.INTRADAY_BID) {
      await transitionIntraday(request)
    } else if (task.businessProcess === BusinessProcess.BLOCK_BID) {
      await transitionBlock(request)
    } else {
      // 未知の業務は RPC を呼ばず error toast に倒す (= 取りこぼしで成功 toast を出さない)
      throw new Error(`unsupported businessProcess: ${task.businessProcess}`)
    }
  }

  /**
   * (a)(b) 締切 / GC の後で承認・差戻しが実行されなかった時 (AC-APT-022): ダイアログを閉じ、
   * 一覧を読み込み直して、 一覧の画面にエラーとして出す。 札は失効して一覧から消える (AC-APT-002)。
   */
  const closeWithListError = async (message: string): Promise<void> => {
    setDialogState({ kind: 'none' })
    setApproveBlockedSections([])
    await invalidateApprovalTasks()
    setListError(message)
  }

  /**
   * 承認・差戻しの実行。 実行されなかった理由で振る舞いを分ける (AC-APT-022):
   * (a)(b) 締切 / GC → ダイアログを閉じて一覧のエラーに出す、
   * (c)(d) 入札制限違反 / 自動取消時刻 → ダイアログを開いたままコメントを保持して理由を出す、
   * それ以外 → AC-APT-012 のトースト (ダイアログも開いたまま)。
   */
  const decide = async (
    task: ApprovalTask,
    action: DecisionAction,
    reason: string | undefined,
  ): Promise<void> => {
    setListError(undefined)
    setApproveBlockedSections([])

    // スポット・ブロックの入札締切後は、 backend が拒否する (#6647) のを待たずに実行しない。
    // 確認 UI を開いたまま締切を跨いだ場合もここで止まる。
    if (isPastSpotDeadline(task, serverNow())) {
      await closeWithListError(
        closedDecisionMessage({ kind: 'spotDeadlinePassed' }, t),
      )
      return
    }

    try {
      await transitionTask(task, action, reason)
    } catch (err) {
      const failure = classifyDecisionFailure(task, action, err, t)
      switch (failure.kind) {
        case 'spotDeadlinePassed':
        case 'intradayGcPassed':
          await closeWithListError(closedDecisionMessage(failure, t))
          return
        case 'autoCancelPassed':
          setApproveBlockedSections([{ title: failure.message, details: [] }])
          return
        case 'bidLimitViolation': {
          // 入札画面と同じ違反サマリ (対象エリア・設定上限/下限・実際の入札値、 AC-BSP-097 /
          // AC-BIN-097) を、 各画面と同じ組み立てで出す。 材料を取れない時は理由だけを伝える。
          const sections: ViolationSection[] | undefined =
            task.businessProcess === BusinessProcess.INTRADAY_BID
              ? await buildIntradayViolationSection(task, err).then((v) =>
                  v === undefined ? undefined : [v],
                )
              : await buildSpotBlockViolationSections(task)
          setApproveBlockedSections(
            sections ?? [{ title: failure.message, details: [] }],
          )
          return
        }
        case 'other':
          showErrorToast({ title: failure.message })
          return
      }
    }
    await invalidateApprovalTasks()
    setDialogState({ kind: 'none' })
    showSuccessToast({
      title: t(
        action === 'approve'
          ? 'page:adminApprovalTasks.toast.approveSuccess'
          : 'page:adminApprovalTasks.toast.rejectSuccess',
      ),
    })
  }

  const handleApprove = async (comment?: string): Promise<void> => {
    if (dialogState.kind !== 'approve') return
    await decide(dialogState.task, 'approve', comment)
  }

  const handleReject = async (reason: string): Promise<void> => {
    if (dialogState.kind !== 'reject') return
    await decide(dialogState.task, 'reject', reason)
  }

  /**
   * 承認・差戻しボタンの押下。 承認者でない人・申請者本人は、 確認モーダルで明示的に再確認して
   * から承認確認ダイアログ・差戻し確認ダイアログへ進む (AC-APT-019a)。 それ以外は直接ダイアログへ。
   */
  const startDecision = (task: ApprovalTask, action: DecisionAction): void => {
    setListError(undefined)
    setApproveBlockedSections([])
    const identity = approverIdentity(task)
    if (identity.isNotApprover || identity.isSelfApproval) {
      setGuardState({ action, task })
      return
    }
    setDialogState({ kind: action, task })
  }

  const guardMessages =
    guardState === undefined
      ? []
      : buildApproverGuardMessages(
          approverIdentity(guardState.task),
          guardState.action,
          t,
        )

  const getBusinessProcessLabel = (bp: BusinessProcess): string => {
    switch (bp) {
      case BusinessProcess.SPOT_BID:
        return t('page:adminApprovalTasks.businessProcess.spotBid')
      case BusinessProcess.INTRADAY_BID:
        return t('page:adminApprovalTasks.businessProcess.intradayBid')
      case BusinessProcess.BLOCK_BID:
        return t('page:adminApprovalTasks.businessProcess.blockBid')
      default:
        return '—'
    }
  }

  // 状態フィルタの選択肢。 allowedStates 指定時はその集合のみ。
  const stateOptionDefs: Array<{ state: BiddingApprovalState; key: string }> = [
    { state: BiddingApprovalState.PENDING, key: 'pending' },
    { state: BiddingApprovalState.APPROVED, key: 'approved' },
    { state: BiddingApprovalState.REJECTED, key: 'rejected' },
  ]
  const approvalStateOptions: SelectOption[] = [
    { value: '', label: t('page:adminApprovalTasks.filter.statusAll') },
    ...stateOptionDefs
      .filter(
        (o) => allowedStates === undefined || allowedStates.includes(o.state),
      )
      .map((o) => ({
        value: String(o.state),
        label: t(`page:adminApprovalTasks.approvalState.${o.key}`),
      })),
  ]

  const businessProcessOptions: SelectOption[] = [
    {
      value: '',
      label: t('page:adminApprovalTasks.filter.businessProcessAll'),
    },
    {
      value: String(BusinessProcess.SPOT_BID),
      label: t('page:adminApprovalTasks.businessProcess.spotBid'),
    },
    {
      value: String(BusinessProcess.INTRADAY_BID),
      label: t('page:adminApprovalTasks.businessProcess.intradayBid'),
    },
    {
      value: String(BusinessProcess.BLOCK_BID),
      label: t('page:adminApprovalTasks.businessProcess.blockBid'),
    },
  ]

  const columns: Array<MasterDataTableColumn<ApprovalTask>> = [
    {
      key: 'requestedAt',
      header: t('page:adminApprovalTasks.column.requestedAt'),
      cell: (row) => formatRequestedAt(row),
    },
    {
      key: 'businessProcess',
      header: t('page:adminApprovalTasks.column.businessProcess'),
      cell: (row) => getBusinessProcessLabel(row.businessProcess),
    },
    {
      key: 'requestedBy',
      header: t('page:adminApprovalTasks.column.requestedBy'),
      cell: (row) => resolveDisplayName(row.requestedBy) ?? '—',
    },
    {
      key: 'itemCount',
      header: t('page:adminApprovalTasks.column.itemCount'),
      align: 'right',
      cell: (row) => String(row.itemCount),
    },
    {
      key: 'approvalState',
      header: t('page:adminApprovalTasks.column.approvalState'),
      cell: (row) => getApprovalStateLabel(row.approvalState, t),
    },
    {
      key: 'approvedBy',
      header: t('page:adminApprovalTasks.column.approvedBy'),
      cell: (row) => resolveDisplayName(row.approvedBy) ?? '—',
    },
  ]

  const hasRowNotice =
    !readOnly &&
    filteredTasks.some(
      (task) =>
        task.approvalState === BiddingApprovalState.PENDING &&
        buildApproverStandingNotices(approverIdentity(task), t).length > 0,
    )

  const renderRowActions = (task: ApprovalTask): ReactNode => {
    // read-only 表示 (spot 埋め込み) では承認/差戻しアクションを出さない。
    // 承認操作は申請者自身の画面ではなく `/admin/approval-tasks` に集約する。
    if (readOnly) {
      return null
    }
    if (task.approvalState !== BiddingApprovalState.PENDING) {
      return null
    }
    // 承認者でない人・申請者本人にも押せるまま出し、 ボタンの近くに常時注意を出す (AC-APT-019a)。
    const identity = approverIdentity(task)
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          gap: '4px',
        }}
      >
        <div
          style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}
        >
          <PrimaryButton
            disabled={isActionPending}
            onClick={() => {
              startDecision(task, 'approve')
            }}
            size="sm"
          >
            {t('page:adminApprovalTasks.action.approve')}
          </PrimaryButton>
          <SecondaryButton
            disabled={isActionPending}
            onClick={() => {
              startDecision(task, 'reject')
            }}
            size="sm"
          >
            {t('page:adminApprovalTasks.action.reject')}
          </SecondaryButton>
        </div>
        <ApproverRowNotice identity={identity} />
      </div>
    )
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '1rem',
      }}
    >
      {/* (a)(b) 締切 / GC の後で承認・差戻しが実行されなかった旨 (AC-APT-022)。 一覧の最上部に出す。 */}
      {listError !== undefined && (
        <CompactAlert role="alert" status="error" title={listError} />
      )}

      {/* フィルタバー (全フィルタ非表示時は描画しない) */}
      {!(hideStateFilter && hideBusinessFilter) && (
        <FilterToolbar>
          {/* 状態フィルタ (spot 埋め込み時は非表示) */}
          {!hideStateFilter && (
            <FilterSection
              label={t('page:adminApprovalTasks.filter.statusLabel')}
            >
              <Box minWidth="170px" css={selectFontFix}>
                <Select
                  isMulti={false}
                  // 一覧の sticky TH (MasterDataTable zIndex:1) に menu が覆われるため
                  // body へ portal する。
                  menuPortalTarget={document.body}
                  onChange={(opt: SelectOption | null) => {
                    const val = opt?.value ?? ''
                    setFilter((prev) => ({
                      ...prev,
                      approvalState:
                        val === ''
                          ? undefined
                          : (Number(val) as BiddingApprovalState),
                    }))
                  }}
                  options={approvalStateOptions}
                  selectedOptions={
                    approvalStateOptions.find(
                      (o) =>
                        o.value ===
                        (filter.approvalState !== undefined
                          ? String(filter.approvalState)
                          : ''),
                    ) ?? null
                  }
                  size="md"
                  styles={{ menuPortal: (base) => ({ ...base, zIndex: 1500 }) }}
                />
              </Box>
            </FilterSection>
          )}

          {/* 業務フィルタ (spot 埋め込み時は非表示) */}
          {!hideBusinessFilter && (
            <FilterSection
              label={t('page:adminApprovalTasks.filter.businessProcessLabel')}
            >
              <Box minWidth="190px" css={selectFontFix}>
                <Select
                  isMulti={false}
                  // 一覧の sticky TH (MasterDataTable zIndex:1) に menu が覆われるため
                  // body へ portal する。
                  menuPortalTarget={document.body}
                  onChange={(opt: SelectOption | null) => {
                    const val = opt?.value ?? ''
                    setFilter((prev) => ({
                      ...prev,
                      businessProcess:
                        val === ''
                          ? undefined
                          : (Number(val) as BusinessProcess),
                    }))
                  }}
                  options={businessProcessOptions}
                  selectedOptions={
                    businessProcessOptions.find(
                      (o) =>
                        o.value ===
                        (filter.businessProcess !== undefined
                          ? String(filter.businessProcess)
                          : ''),
                    ) ?? null
                  }
                  size="md"
                  styles={{ menuPortal: (base) => ({ ...base, zIndex: 1500 }) }}
                />
              </Box>
            </FilterSection>
          )}
        </FilterToolbar>
      )}

      {/* テーブル */}
      <RegionLoading
        minHeight="200px"
        isLoading={isLoading && data === undefined}
      >
        {isLoading && data === undefined ? null : (
          <MasterDataTable
            // 承認者でない人・申請者本人への注意 (AC-APT-019a) を出す行がある時は、操作列を注意の幅 (320px) まで広げる。
            // 既定の 110px のままだと注意が 4〜5 文字ごとに折り返し、行が 500px 超まで伸びる
            actionColWidth={
              hasRowNotice ? ROW_NOTICE_ACTION_COL_WIDTH : undefined
            }
            columns={columns}
            emptyLabel={t('page:adminApprovalTasks.list.empty')}
            getRowKey={(task) => task.id}
            renderRowActions={renderRowActions}
            rows={filteredTasks}
          />
        )}
      </RegionLoading>

      {/* 承認者でない人・申請者本人への確認モーダル (AC-APT-019a) */}
      <ApproverGuardModal
        action={guardState?.action ?? 'approve'}
        isOpen={guardState !== undefined}
        messages={guardMessages}
        onCancel={() => {
          setGuardState(undefined)
        }}
        onConfirm={() => {
          if (guardState === undefined) return
          setDialogState({ kind: guardState.action, task: guardState.task })
          setGuardState(undefined)
        }}
      />

      {/* 承認確認ダイアログ */}
      <ApproveDialog
        blockedSections={approveBlockedSections}
        isLoading={isActionPending}
        isOpen={dialogState.kind === 'approve'}
        onClose={() => {
          setDialogState({ kind: 'none' })
          setApproveBlockedSections([])
        }}
        onConfirm={(comment) => {
          void handleApprove(comment)
        }}
      />

      {/* 差戻し確認ダイアログ */}
      <RejectDialog
        isLoading={isActionPending}
        isOpen={dialogState.kind === 'reject'}
        onClose={() => {
          setDialogState({ kind: 'none' })
        }}
        onConfirm={(reason) => {
          void handleReject(reason)
        }}
      />
    </div>
  )
}
